import { CardClient as Client } from '@atlaskit/link-provider';
import { APIError } from '@atlaskit/linking-common';
import { type JsonLd } from 'json-ld-types';

import { getPreviewPanelBridgeClient } from '@canvas/api-preview/preview-panel-bridge';
import { DurableCache } from '@canvas/durable-cache';
import { hostNotifier, FailureReasons } from '@canvas/host-notifier';

import { mockDataCardResponse, mockPreviewCardResponse } from './smart-link-client-mocks';
import { type SmartLinkData, type Environment } from './smart-link-client-types';
import { extractAri, extractSupportedViews } from './smart-link-client-utils';

export const hasResolved = (data: JsonLd.Response | undefined) => {
  return !!data;
};

export const hasAccess = (data: JsonLd.Response | undefined) => {
  return data?.meta.access === 'granted';
};

const isAPIError = (err: any): err is APIError => {
  return err && err.name === 'APIError';
};

// There are no `fatal` errors in the object resolver services code but instead these
// exist in the @atlaskit/linking-common library instead. If an error is returned that
// isn't explicitly mapped in their library it will return as fatal
export const isFatalError = (error?: Error) =>
  isAPIError(error) && error.kind === 'fatal';

// After talking to the linking platform, we really only need to check if it's a ResolveUnsupportedError. Everything else should be retried by us
// export type APIErrorKind = 'fatal' | 'auth' | 'error' | 'fallback';
// https://stash.atlassian.com/projects/ATLASSIAN/repos/atlassian-frontend-monorepo/browse/platform/packages/linking-platform/smart-card/src/index.ts
export const isResolveUnsupportedError = (error?: Error) =>
  isAPIError(error) && error.type === 'ResolveUnsupportedError';

type UpdateListener = (
  newData: JsonLd.Response,
  oldData: JsonLd.Response | undefined,
) => void;

type UnsubscribeUpdateListener = () => void;

export const CACHE_TIME_UNAUTHORIZED = 1_000;
export const CACHE_TIME_RESOLVED = 120_000;

export class SmartLinkClient extends Client {
  pendingRequests = new Map();

  cache = new DurableCache<SmartLinkData>({
    cacheName: 'smart-links',
    loadFromPersistentCache: true,
  });

  private readonly listeners = new Map<string, Set<UpdateListener>>();

  static create(env: Environment = undefined) {
    return new SmartLinkClient(env);
  }

  public async prepare(currentUser: string) {
    await this.cache.prepare(currentUser);
  }

  public prefetchData = () => Promise.resolve(undefined);

  /**
   * Fetches smart link data for a given URL.
   *
   * if the response doesn't exist or if the cache is stale,
   * it refreshes the data and returns the response.
   *
   * if the cache item is valid the response is returned from cache.
   *
   * This function is intended to be called as often as needed
   * as requests are deduplicated and batched internally.
   *
   * @see forceRefreshData if you want to ensure fresh data is retrieved
   */
  public async fetchData(url: string) {
    const entry = this.getCachedData(url);

    if (entry) {
      if (!hasAccess(entry.response) && this.shouldRefresh(url)) {
        return this.forceRefreshData(url);
      }
      return entry.response;
    }

    return this.forceRefreshData(url);
  }

  /**
   * Refreshes smart link data for a given URL, ignoring cache.
   *
   * This function is intended to be called as often as needed
   * as requests are deduplicated internally.
   *
   * @param url The URL to resolve
   * @see fetchData for which accounts for caching
   */
  public async forceRefreshData(url: string) {
    const existingRequest = this.pendingRequests.get(url);
    if (existingRequest) {
      return existingRequest;
    }

    // Grab the cached data before it gets wiped, and provide it to the listeners
    const currData = this.getCachedData(url)?.response;
    this.deleteCachedData(url);

    hostNotifier.reportExternalCheckpoint({
      source: FailureReasons.SMART_LINK_RESOLVE_FAILURE,
      location: 'smart-link-client',
      endpoint: '/gateway/api/object-resolver/resolve/batch',
    });

    const previewPanelBridge = getPreviewPanelBridgeClient();

    const newRequest = this.fetchWithIgnoreCache(url)
      .then(async (response) => {
        const data = {
          supportedViews: extractSupportedViews(response),
          response,
          timestamp: Date.now(),
        };

        const nextData = data.response;

        const ari = extractAri(data.response);
        if (ari) {
          const isPreviewPanelAvailable =
            await previewPanelBridge.isPreviewPanelAvailable({
              ari: ari,
            });

          data.response.meta['whiteboards:previewPanelSupported'] =
            isPreviewPanelAvailable;
        }

        this.notifyListeners(url, nextData, currData);

        this.setCachedData(url, data);
        this.pendingRequests.delete(url);

        return data.response;
      })
      .catch((e) => {
        hostNotifier.reportExternalCheckpoint({
          source: FailureReasons.SMART_LINK_RESOLVE_FAILURE,
          location: 'smart-link-client',
          endpoint: '/gateway/api/object-resolver/resolve/batch',
        });

        // This is a bit hacky but basically if we receive a `fatal` error then the CardSSR
        // component we use will explode. HOWEVER, if we change it to `error` then we can
        // show the error state instead and handle these kinds of problems gracefully.
        // The ONLY scenario where we can receive `fatal` errors is if the @atlaskit/linking-common
        // receives an error type that hasn't been handled. These are retriable for us.
        if (!isResolveUnsupportedError(e) && isFatalError(e)) {
          // Annoyingly, we aren't throwing the original since the `kind` is readonly which
          // means we may lose a bit of the stack trace from inside `fetchData`
          throw new APIError('error', e.hostname, e.message, e.type, e.extensionKey);
        }

        // Throw normally if it's not 'fatal'
        throw e;
      });

    this.pendingRequests.set(url, newRequest);
    return newRequest;
  }

  /**
   * Fetches smart link data for a given URL, ignoring cache. For some reason,
   * the CardClient (super.fetchData) method does not ignore cache even with
   * ignoreCachedValue set to true. This method is used to bypass this issue.
   *
   * @param url The URL to resolve
   * @returns The resolved data
   */
  private async fetchWithIgnoreCache(url: string): Promise<JsonLd.Response> {
    return await fetch('/gateway/api/object-resolver/resolve/batch', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify([{ resourceUrl: url, ignoreCachedValue: true }]),
    }).then(async (res) => {
      const [data] = await res.json();
      if (data.error) {
        throw new APIError(
          'fallback',
          url,
          data.error.message ?? 'Unknown error',
          data.error.type,
        );
      }
      return data.body;
    });
  }

  /**
   * Returns the cached data immediately.
   * This data is never guaranteed to exist
   * so use with care to avoid accidentally creating race conditions.
   */
  public getCachedData(url: string) {
    return this.cache.get(url);
  }

  public setCachedData(url: string, data: SmartLinkData) {
    this.cache.set(url, data);
  }

  public notifyListeners(
    url: string,
    nextData: JsonLd.Response,
    currData?: JsonLd.Response,
  ) {
    const listeners = this.listeners.get(url);
    listeners?.forEach((listener) => listener(nextData, currData));
  }

  public deleteCachedData(url: string) {
    this.cache.delete(url);
  }

  /**
   * @param url The smart link URL to listen to
   * @param callback Will be called when the client has fetched fresh data
   * @returns A callback that you can use to detach the listener.
   * Note that this must be done in order to avoid memory leaks
   */
  public subscribeToUpdates(
    url: string,
    callback: UpdateListener,
  ): UnsubscribeUpdateListener {
    const callbacks = this.listeners.get(url) ?? new Set();

    if (callbacks.has(callback)) {
      throw new Error(`The same callback was added to ${url} more than once`);
    }

    callbacks.add(callback);

    this.listeners.set(url, callbacks);

    return () => {
      callbacks.delete(callback);
    };
  }

  public shouldRefresh(url: string) {
    const entry = this.cache.get(url);
    if (!entry) {
      return true;
    } else if (!hasAccess(entry.response)) {
      return Date.now() - entry.timestamp > CACHE_TIME_UNAUTHORIZED;
    } else {
      return Date.now() - entry.timestamp > CACHE_TIME_RESOLVED;
    }
  }
}

/* Difficult to mock module without test client in same file  */
export class SmartLinkTestClient {
  static create(env: Environment = undefined) {
    const client = SmartLinkClient.create(env);

    const dataCardUrl = 'https://fabric-data-card.com/';
    client.setCachedData(dataCardUrl, {
      response: mockDataCardResponse(dataCardUrl),
      supportedViews: ['block'],
      timestamp: Date.now(),
    });

    const previewCardUrl = 'https://preview-card.com/';
    client.setCachedData(previewCardUrl, {
      response: mockPreviewCardResponse(previewCardUrl),
      supportedViews: ['block'],
      timestamp: Date.now(),
    });

    const jiraIssueUrl = 'https://hello.atlassian.net/browse/AUT-14';
    client.setCachedData(jiraIssueUrl, {
      response: mockPreviewCardResponse(jiraIssueUrl, 'jira-object-provider'),
      supportedViews: ['block'],
      timestamp: Date.now(),
    });

    const jiraIssueUrl2 = 'https://hello.atlassian.net/browse/AUT-15';
    client.setCachedData(jiraIssueUrl2, {
      response: mockPreviewCardResponse(jiraIssueUrl2, 'jira-object-provider'),
      supportedViews: ['block'],
      timestamp: Date.now(),
    });

    return client;
  }
}

export const smartLinkClient =
  process.env.MODE === 'test' ? SmartLinkTestClient.create() : SmartLinkClient.create();

export type { SmartLinkData } from './smart-link-client-types';
export {
  mockDataCardResponse,
  mockUnauthedDataCardResponse,
} from './smart-link-client-mocks';
export { createUnauthorizedResponse } from './smart-link-client-utils';
